import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { differenceInCalendarDays, parseISO, format } from "date-fns";
import { de, enUS, tr, ar } from "date-fns/locale";
import {
  ListChecks, Calendar, Euro, Bell, History, Check, Plus, RefreshCw, Clock, X, Trash2,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function daysLabel(dateIso, t) {
  if (!dateIso) return null;
  const n = differenceInCalendarDays(parseISO(dateIso), new Date());
  if (n < 0) return { txt: t("e3.overdue"), warn: true };
  if (n === 0) return { txt: t("e3.today"), warn: true };
  return { txt: t("e3.daysLeft").replace("{n}", n), warn: n <= 3 };
}

function Section({ icon: Icon, title, action, children, testid }) {
  return (
    <section data-testid={testid} className="mt-6">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 font-display text-sm font-bold uppercase tracking-wide text-[#4A6358]">
          <Icon size={15} /> {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

const Empty = ({ label }) => (
  <p className="rounded-xl border border-[#E2EAE5] bg-white/60 px-3 py-3 text-xs text-[#B0C2B8]">{label}</p>
);

export function CaseWorkspace({ caseData }) {
  const { t, lang } = useI18n();
  const LOC = { de, en: enUS, tr, ar };
  const fmt = (iso) => {
    if (!iso) return iso;
    try { return format(parseISO(iso), "dd.MM.yyyy", { locale: LOC[lang] || enUS }); } catch { return iso; }
  };
  const qc = useQueryClient();
  const navigate = useNavigate();
  const caseId = caseData.id;
  const docs = caseData.documents || [];

  const useCaseQuery = (key, url) => useQuery({ queryKey: [key, caseId], queryFn: async () => (await api.get(url)).data });
  const actions = useQuery({ queryKey: ["actions", caseId, lang], queryFn: async () => (await api.get(`/cases/${caseId}/action-items?language=${lang}`)).data });
  const deadlines = useQuery({ queryKey: ["deadlines", caseId, lang], queryFn: async () => (await api.get(`/cases/${caseId}/deadlines?language=${lang}`)).data });
  const financial = useCaseQuery("financial", `/cases/${caseId}/financial-obligations`);
  const reminders = useCaseQuery("reminders", `/cases/${caseId}/reminders`);
  const events = useCaseQuery("events", `/cases/${caseId}/events`);

  const invalidateAll = () => ["actions", "deadlines", "financial", "reminders", "events"].forEach((k) => qc.invalidateQueries({ queryKey: [k, caseId] }));

  const [newAction, setNewAction] = useState("");
  const [showPay, setShowPay] = useState(false);
  const [pay, setPay] = useState({ creditor: "", amount: "", due_date: "", source_type: "MANUAL" });

  const syncMut = useMutation({
    mutationFn: async () => { for (const d of docs) await api.post(`/cases/${caseId}/sync-from-document/${d.id}`); },
    onSuccess: () => { toast.success(t("e3.synced")); invalidateAll(); },
    onError: (e) => toast.error(apiError(e, t)),
  });
  const addActionMut = useMutation({
    mutationFn: async () => (await api.post(`/cases/${caseId}/action-items`, { title: newAction })).data,
    onSuccess: () => { setNewAction(""); invalidateAll(); },
    onError: (e) => toast.error(apiError(e, t)),
  });
  const toggleActionMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/action-items/${id}`, { status })).data,
    onSuccess: invalidateAll,
  });
  const addPayMut = useMutation({
    mutationFn: async () => (await api.post(`/cases/${caseId}/financial-obligations`, pay)).data,
    onSuccess: () => { setShowPay(false); setPay({ creditor: "", amount: "", due_date: "", source_type: "MANUAL" }); invalidateAll(); },
    onError: (e) => toast.error(apiError(e, t)),
  });
  const remindMut = useMutation({
    mutationFn: async ({ target_type, target_id, offset_days }) => (await api.post(`/reminders`, { case_id: caseId, target_type, target_id, offset_days })).data,
    onSuccess: () => { toast.success(t("e3.event.reminder_created")); invalidateAll(); },
    onError: (e) => toast.error(apiError(e, t)),
  });
  const dismissRemMut = useMutation({
    mutationFn: async (id) => (await api.patch(`/reminders/${id}`, { status: "DISMISSED" })).data,
    onSuccess: invalidateAll,
  });
  const editRemMut = useMutation({
    mutationFn: async ({ id, offset_days }) => (await api.patch(`/reminders/${id}`, { offset_days })).data,
    onSuccess: invalidateAll,
    onError: (e) => toast.error(apiError(e, t)),
  });
  const deleteRemMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/reminders/${id}`)).data,
    onSuccess: invalidateAll,
    onError: (e) => toast.error(apiError(e, t)),
  });

  const ST_BADGE = { EXPLICIT_DOCUMENT: { bg: "#EDF7F2", c: "#1B6943" }, HISTORICAL_ESTIMATE: { bg: "#FFF8EC", c: "#B36B00" }, MANUAL: { bg: "#F1F3F2", c: "#5A625E" } };

  return (
    <div>
      {docs.length > 0 && (
        <button
          data-testid="case-sync-button"
          onClick={() => syncMut.mutate()}
          disabled={syncMut.isPending}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl border border-[#C4DFD2] bg-[#F2F7F4] py-2.5 text-sm font-semibold text-[#2D5A4C] active:scale-[0.99]"
        >
          <RefreshCw size={15} className={syncMut.isPending ? "animate-spin" : ""} /> {t("e3.syncFromDocs")}
        </button>
      )}

      {/* Deadlines */}
      <Section testid="case-deadlines" icon={Calendar} title={t("e3.deadlines")}>
        {(deadlines.data || []).length === 0 ? <Empty label={t("e3.none")} /> : (
          <div className="space-y-2">
            {deadlines.data.map((d) => {
              const dl = daysLabel(d.date, t);
              return (
                <div key={d.id} data-testid={`deadline-${d.id}`} className="rounded-2xl border border-[#E2EAE5] bg-white p-3">
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-[#1A2E26]">{d.title}</p>
                      <p className="text-[11px] text-[#7A9488]">{d.date ? fmt(d.date) : d.date_text}</p>
                    </div>
                    {dl && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${dl.warn ? "bg-[#FDF2F0] text-[#C03A2B]" : "bg-[#EDF7F2] text-[#1B6943]"}`}>{dl.txt}</span>}
                  </div>
                  <div className="mt-2 flex items-center gap-1.5">
                    <span className="text-[10px] text-[#7A9488]">{t("e3.remind")}:</span>
                    {[1, 2, 3, 7].map((n) => (
                      <button key={n} data-testid={`deadline-remind-${d.id}-${n}`} onClick={() => remindMut.mutate({ target_type: "deadline", target_id: d.id, offset_days: n })}
                        className="rounded-full border border-[#E2EAE5] px-2 py-0.5 text-[10px] font-semibold text-[#2D5A4C] hover:bg-[#F0F4F1]">{n}{t("e3.dayShort")}</button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Payments */}
      <Section testid="case-payments" icon={Euro} title={t("e3.payments")}
        action={<button data-testid="case-add-payment" onClick={() => setShowPay((s) => !s)} className="flex items-center gap-1 text-xs font-semibold text-[#2D5A4C]"><Plus size={13} />{t("e3.addPayment")}</button>}>
        {showPay && (
          <div className="mb-2 space-y-2 rounded-2xl border border-[#E2EAE5] bg-white p-3">
            <Input data-testid="pay-creditor" className="h-10 rounded-lg" placeholder={t("e3.creditor")} value={pay.creditor} onChange={(e) => setPay({ ...pay, creditor: e.target.value })} />
            <Input data-testid="pay-amount" className="h-10 rounded-lg" placeholder="286,40 EUR" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
            <Input data-testid="pay-date" type="date" className="h-10 rounded-lg" value={pay.due_date} onChange={(e) => setPay({ ...pay, due_date: e.target.value })} />
            <Select value={pay.source_type} onValueChange={(v) => setPay({ ...pay, source_type: v })}>
              <SelectTrigger data-testid="pay-sourcetype" className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="MANUAL">{t("e3.MANUAL")}</SelectItem>
                <SelectItem value="HISTORICAL_ESTIMATE">{t("e3.HISTORICAL_ESTIMATE")}</SelectItem>
                <SelectItem value="EXPLICIT_DOCUMENT">{t("e3.EXPLICIT_DOCUMENT")}</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="pay-save" onClick={() => addPayMut.mutate()} disabled={!pay.creditor || !pay.amount} className="h-9 w-full rounded-lg bg-[#2D5A4C] text-xs">{t("e3.save")}</Button>
          </div>
        )}
        {(financial.data || []).length === 0 ? <Empty label={t("e3.none")} /> : (
          <div className="space-y-2">
            {financial.data.map((f) => {
              const est = f.source_type === "HISTORICAL_ESTIMATE";
              const b = ST_BADGE[f.source_type] || ST_BADGE.MANUAL;
              return (
                <div key={f.id} data-testid={`payment-${f.id}`} className="rounded-2xl border border-[#E2EAE5] bg-white p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm font-semibold text-[#1A2E26]">{f.creditor}</p>
                    <p className="shrink-0 font-mono text-sm font-bold text-[#1A2E26]">{f.amount}{est ? " *" : ""}</p>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ backgroundColor: b.bg, color: b.c }}>{t(`e3.${f.source_type}`)}</span>
                    {f.due_date && <span className="text-[10px] text-[#7A9488]">{t("e3.dueOn")} {fmt(f.due_date)}</span>}
                  </div>
                  {est && f.based_on_date && <p className="mt-1 text-[11px] italic text-[#B36B00]">{t("e3.estimateNote")} {fmt(f.based_on_date)}</p>}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Action items */}
      <Section testid="case-actions" icon={ListChecks} title={t("e3.actions")}>
        <div className="mb-2 flex gap-2">
          <Input data-testid="action-input" className="h-10 rounded-lg" placeholder={t("e3.addAction")} value={newAction} onChange={(e) => setNewAction(e.target.value)} />
          <Button data-testid="action-add" onClick={() => addActionMut.mutate()} disabled={!newAction.trim()} className="h-10 rounded-lg bg-[#2D5A4C] px-3"><Plus size={16} /></Button>
        </div>
        {(actions.data || []).length === 0 ? <Empty label={t("e3.none")} /> : (
          <div className="space-y-2">
            {actions.data.map((a) => {
              const done = a.status === "DONE";
              return (
                <button key={a.id} data-testid={`action-${a.id}`} onClick={() => toggleActionMut.mutate({ id: a.id, status: done ? "OPEN" : "DONE" })}
                  className="flex w-full items-center gap-3 rounded-2xl border border-[#E2EAE5] bg-white p-3 text-left">
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${done ? "border-[#2D5A4C] bg-[#2D5A4C] text-white" : "border-[#C4DFD2]"}`}>{done && <Check size={13} />}</span>
                  <span className={`min-w-0 flex-1 text-sm ${done ? "text-[#B0C2B8] line-through" : "text-[#1A2E26]"}`}>{a.title}</span>
                </button>
              );
            })}
          </div>
        )}
      </Section>

      {/* Reminders */}
      {(reminders.data || []).length > 0 && (
        <Section testid="case-reminders" icon={Bell} title={t("e3.reminders")}>
          <div className="space-y-2">
            {reminders.data.map((r) => (
              <div key={r.id} data-testid={`reminder-${r.id}`} className="rounded-2xl border border-[#E2EAE5] bg-white p-3">
                <div className="flex items-center gap-2">
                  <Clock size={15} className="shrink-0 text-[#2D5A4C]" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[#1A2E26]">{r.target_label || r.target_type}</p>
                    <p className="text-[11px] text-[#7A9488]">{t("e3.remindOn")} {fmt(r.remind_date)}</p>
                  </div>
                  <button data-testid={`reminder-delete-${r.id}`} onClick={() => deleteRemMut.mutate(r.id)} className="p-1 text-[#C03A2B]"><Trash2 size={15} /></button>
                  {r.status === "PENDING" && (
                    <button data-testid={`reminder-dismiss-${r.id}`} onClick={() => dismissRemMut.mutate(r.id)} className="p-1 text-[#7A9488]"><X size={15} /></button>
                  )}
                </div>
                {r.target_type !== "followup" && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] text-[#7A9488]">{t("e3.remind")}:</span>
                    {[1, 3, 7, 14].map((n) => (
                      <button key={n} data-testid={`reminder-offset-${r.id}-${n}`} onClick={() => editRemMut.mutate({ id: r.id, offset_days: n })}
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${r.offset_days === n ? "border-[#2D5A4C] bg-[#F0F4F1] text-[#2D5A4C]" : "border-[#E2EAE5] text-[#2D5A4C] hover:bg-[#F0F4F1]"}`}>{n}{t("e3.dayShort")}</button>
                    ))}
                    <span className="text-[10px] text-[#7A9488]">{t("e5.remindDaysBefore")}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Timeline */}
      <Section testid="case-timeline" icon={History} title={t("e3.timeline")}>
        {(events.data || []).length === 0 ? <Empty label={t("e3.none")} /> : (
          <ol className="relative space-y-3 border-l border-[#E2EAE5] pl-4">
            {events.data.map((e) => (
              <li key={e.id} data-testid={`event-${e.id}`} className="relative">
                <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-[#2D5A4C]" />
                <p className="text-sm font-medium text-[#1A2E26]">{t(`e3.event.${e.type}`, e.type.replace(/_/g, " "))}</p>
                <p className="text-[11px] text-[#7A9488]">{new Date(e.created_at).toLocaleString()}</p>
              </li>
            ))}
          </ol>
        )}
      </Section>
    </div>
  );
}
